import React, { useState } from 'react';
import { Send, ChevronDown, ChevronUp, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DOMAIN_OPTIONS, AUDIENCE_OPTIONS, SENSITIVITY_OPTIONS, ACTIONS } from '../../design-system/content';

export function InputPanel({
  text, setText,
  sessionMode, setSessionMode,
  context, setContext,
  onAnalyze, loading, hasResult,
  onClear,
}) {
  const [showContext, setShowContext] = useState(false);

  return (
    <div className="space-y-4">
      {/* Storage mode */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          {sessionMode === 'ephemeral'
            ? <EyeOff className="w-3.5 h-3.5 text-[var(--msml-text-muted)]" />
            : <Eye className="w-3.5 h-3.5 text-[var(--msml-primary)]" />}
          <span className="text-xs text-[var(--msml-text-secondary)]">
            {sessionMode === 'ephemeral' ? 'Ephemeral' : 'Persisted'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="ws-mode" className="text-xs text-[var(--msml-text-muted)]">Save</Label>
          <Switch
            id="ws-mode"
            checked={sessionMode === 'persisted'}
            onCheckedChange={c => setSessionMode(c ? 'persisted' : 'ephemeral')}
            data-testid="storage-mode-toggle"
          />
        </div>
      </div>

      {/* Main text input */}
      <div className="msml-card !p-4 !gap-3">
        <div className="flex items-center justify-between">
          <label className="msml-label" data-testid="input-label">AI Output</label>
          {text && (
            <button onClick={onClear} className="text-xs text-[var(--msml-text-muted)] hover:text-white transition-colors" data-testid="clear-btn">
              Clear
            </button>
          )}
        </div>
        <Textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Paste AI-generated text here for analysis..."
          className="msml-input min-h-[160px] lg:min-h-[200px] resize-none text-sm leading-relaxed"
          data-testid="text-input"
        />

        {/* Optional user prompt */}
        <div>
          <label className="text-xs text-[var(--msml-text-muted)] mb-1 block">Original prompt (optional)</label>
          <Textarea
            value={context.userPrompt || ''}
            onChange={e => setContext(prev => ({ ...prev, userPrompt: e.target.value }))}
            placeholder="What prompt generated this AI output?"
            className="msml-input min-h-[60px] resize-none text-xs leading-relaxed"
            data-testid="user-prompt-input"
          />
        </div>

        {/* Context expander */}
        <button
          onClick={() => setShowContext(!showContext)}
          className="flex items-center gap-1.5 text-xs text-[var(--msml-text-muted)] hover:text-[var(--msml-text-secondary)] transition-colors w-fit"
          data-testid="context-toggle"
        >
          {showContext ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Context options
        </button>

        {showContext && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 msml-fade-in">
            <div>
              <Label className="text-xs text-[var(--msml-text-muted)] mb-1 block">Domain</Label>
              <Select value={context.domain || ''} onValueChange={v => setContext(prev => ({ ...prev, domain: v }))}>
                <SelectTrigger className="msml-input text-xs h-8" data-testid="domain-select">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  {DOMAIN_OPTIONS.map(o => <SelectItem key={o.value} value={o.value || 'auto'}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[var(--msml-text-muted)] mb-1 block">Audience</Label>
              <Select value={context.audience || ''} onValueChange={v => setContext(prev => ({ ...prev, audience: v }))}>
                <SelectTrigger className="msml-input text-xs h-8" data-testid="audience-select">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value || 'auto'}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[var(--msml-text-muted)] mb-1 block">Sensitivity</Label>
              <Select value={context.sensitivity || ''} onValueChange={v => setContext(prev => ({ ...prev, sensitivity: v }))}>
                <SelectTrigger className="msml-input text-xs h-8" data-testid="sensitivity-select">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  {SENSITIVITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value || 'auto'}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Analyze button */}
        <Button
          onClick={onAnalyze}
          disabled={!text.trim() || loading}
          className="msml-btn-primary w-full mt-1"
          data-testid="analyze-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
          ) : (
            <><Send className="w-4 h-4 mr-2" />Analyze</>
          )}
        </Button>
      </div>
    </div>
  );
}
